/**
 * Validation utility for TeeSync forms and inputs
 */
import React from 'react'

export const ValidationRules = {
  required: (message = 'This field is required') => ({
    rule: (value) => value != null && value.toString().trim() !== '',
    message
  }),

  minLength: (min, message) => ({
    rule: (value) => value && value.length >= min,
    message: message || `Must be at least ${min} characters long`
  }),

  maxLength: (max, message) => ({
    rule: (value) => !value || value.length <= max,
    message: message || `Must be no more than ${max} characters long`
  }),

  email: (message = 'Please enter a valid email address') => ({
    rule: (value) => {
      if (!value) return true // Optional validation - use required() for mandatory emails
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(value)
    },
    message
  }),

  inviteCode: (message = 'Invite code must be 8 characters long') => ({
    rule: (value) => {
      if (!value) return false
      const cleaned = value.replace(/[^A-Z0-9]/g, '')
      return cleaned.length === 8
    },
    message
  }),

  groupName: (message = 'Group name must be between 2 and 50 characters') => ({
    rule: (value) => {
      if (!value || !value.trim()) return false
      const trimmed = value.trim()
      return trimmed.length >= 2 && trimmed.length <= 50
    },
    message
  }),

  password: (message = 'Password must be at least 8 characters long') => ({
    rule: (value) => value && value.length >= 8,
    message
  }),

  confirmPassword: (originalPassword, message = 'Passwords do not match') => ({
    rule: (value) => value === originalPassword,
    message
  }),

  phoneNumber: (message = 'Please enter a valid phone number') => ({
    rule: (value) => {
      if (!value) return true // Optional validation
      const phoneRegex = /^[\+]?[\d\s\-\(\)]{10,}$/
      return phoneRegex.test(value)
    },
    message
  }),

  url: (message = 'Please enter a valid URL') => ({
    rule: (value) => {
      if (!value) return true // Optional validation
      try {
        new URL(value)
        return true
      } catch {
        return false
      }
    },
    message
  })
}

/**
 * Creates a validation schema for a form
 */
export class ValidationSchema {
  constructor(fields = {}) {
    this.fields = fields
  }

  /**
   * Add a field with validation rules
   * @param {string} fieldName - Name of the field
   * @param {Array} rules - Array of validation rules
   */
  addField(fieldName, rules) {
    this.fields[fieldName] = rules
    return this
  }

  /**
   * Validate a single field
   * @param {string} fieldName - Name of the field
   * @param {any} value - Value to validate
   * @returns {object} Validation result with isValid and error message
   */
  validateField(fieldName, value) {
    const rules = this.fields[fieldName] || []
    
    for (const rule of rules) {
      if (!rule.rule(value)) {
        return {
          isValid: false,
          error: rule.message
        }
      }
    }

    return {
      isValid: true,
      error: null
    }
  }

  /**
   * Validate all fields in the form data
   * @param {object} formData - Object containing all form field values
   * @returns {object} Validation result with isValid flag and errors object
   */
  validate(formData) {
    const errors = {}
    let isValid = true

    Object.keys(this.fields).forEach(fieldName => {
      const value = formData[fieldName]
      const fieldValidation = this.validateField(fieldName, value)
      
      if (!fieldValidation.isValid) {
        errors[fieldName] = fieldValidation.error
        isValid = false
      }
    })

    return {
      isValid,
      errors
    }
  }

  /**
   * Get all field names in this schema
   */
  getFieldNames() {
    return Object.keys(this.fields)
  }
}

/**
 * Pre-built validation schemas for common forms
 */
export const ValidationSchemas = {
  createGroup: new ValidationSchema({
    name: [
      ValidationRules.required('Group name is required'),
      ValidationRules.groupName()
    ]
  }),

  joinGroup: new ValidationSchema({
    inviteCode: [
      ValidationRules.required('Invite code is required'),
      ValidationRules.inviteCode()
    ]
  }),

  signUp: new ValidationSchema({
    email: [
      ValidationRules.required('Email is required'),
      ValidationRules.email()
    ],
    password: [
      ValidationRules.required('Password is required'),
      ValidationRules.password()
    ],
    fullName: [
      ValidationRules.required('Full name is required'),
      ValidationRules.minLength(2, 'Name must be at least 2 characters'),
      ValidationRules.maxLength(100, 'Name must be less than 100 characters')
    ]
  }),

  signIn: new ValidationSchema({
    email: [
      ValidationRules.required('Email is required'),
      ValidationRules.email()
    ],
    password: [
      ValidationRules.required('Password is required')
    ]
  }),

  profile: new ValidationSchema({
    fullName: [
      ValidationRules.required('Full name is required'),
      ValidationRules.minLength(2, 'Name must be at least 2 characters'),
      ValidationRules.maxLength(100, 'Name must be less than 100 characters')
    ],
    email: [
      ValidationRules.required('Email is required'),
      ValidationRules.email()
    ],
    phoneNumber: [
      ValidationRules.phoneNumber()
    ]
  }),

  createRound: new ValidationSchema({
    groupId: [
      ValidationRules.required('Please select a group')
    ],
    title: [
      ValidationRules.required('Round title is required'),
      ValidationRules.minLength(3, 'Title must be at least 3 characters'),
      ValidationRules.maxLength(100, 'Title must be less than 100 characters')
    ],
    date: [
      ValidationRules.required('Date is required')
    ],
    startTime: [
      ValidationRules.required('Start time is required')
    ],
    endTime: [
      ValidationRules.required('End time is required')
    ]
  })
}

/**
 * Hook for using validation in React components
 */
export const useValidation = (schema) => {
  const [errors, setErrors] = React.useState({})

  const validateField = (fieldName, value) => {
    const result = schema.validateField(fieldName, value)
    
    setErrors(prev => ({
      ...prev,
      [fieldName]: result.isValid ? null : result.error
    }))

    return result
  }

  const validateForm = (formData) => {
    const result = schema.validate(formData)
    setErrors(result.errors)
    return result
  }

  const clearErrors = () => {
    setErrors({})
  }

  const clearFieldError = (fieldName) => {
    setErrors(prev => ({
      ...prev,
      [fieldName]: null
    }))
  }

  return {
    errors,
    validateField,
    validateForm,
    clearErrors,
    clearFieldError,
    hasErrors: Object.values(errors).some(error => error !== null)
  }
}

/**
 * Utility functions for common validation tasks
 */
export const ValidationUtils = {
  /**
   * Clean and format invite code input
   */
  formatInviteCode: (value) => {
    if (!value) return ''
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
  },

  /**
   * Clean and format group name input
   */
  formatGroupName: (value) => {
    if (!value) return ''
    return value.trim().slice(0, 50)
  },

  /**
   * Clean and format email input
   */
  formatEmail: (value) => {
    if (!value) return ''
    return value.toLowerCase().trim()
  },

  /**
   * Check if a value is considered empty for validation purposes
   */
  isEmpty: (value) => {
    return value == null || 
           (typeof value === 'string' && value.trim() === '') ||
           (Array.isArray(value) && value.length === 0)
  }
}